/**
 * 운영도구 역할(role) 계층 비교 — **FE 게이팅 전용**.
 * ============================================================
 * 설계: `docs/2026-07-07-zoolungman-coin-grant-app-dev-steps.md` §S5 작업 1
 *
 * 🔴 **BE 와 값이 반드시 같아야 합니다** — `api-server/src/services/auth_service.py:19-23`
 *    ```python
 *    ROLE_HIERARCHY = {"PART_TIME": 1, "STAFF": 2, "ADMIN": 3}
 *    ```
 *    한쪽만 바꾸면 "카드는 보이는데 서버가 403" 또는 그 반대(dead-end)가 생깁니다.
 *    BE 를 수정하면 이 파일도 함께 고치고, `utils/roleUtils.test.js` (a) 항목이 그 정합을 지킵니다.
 *
 * ⚠️ **이것은 보안 경계가 아닙니다.** 최종 방어는 서버 `require_role("STAFF")`
 *    (`api-server/src/middleware/jwt_auth.py`) 이며, 여기서 하는 일은 **권한 없는 메뉴를 감춰
 *    dead-end(스캔까지 하고 지급에서만 403)를 없애는 UX 게이팅**입니다.
 *    브라우저 콘솔로 우회 가능하다는 전제 위에서 동작합니다.
 */

/**
 * 역할 → 계층 수치. 숫자가 클수록 상위 권한입니다.
 * (BE `ROLE_HIERARCHY` 와 1:1 — 위 상호 참조 주석 참고)
 */
export const ROLE_LEVEL = Object.freeze({
  PART_TIME: 1,
  STAFF: 2,
  ADMIN: 3,
});

/**
 * 역할 문자열의 계층 수치를 구합니다. **알 수 없으면 0**(어떤 기준선도 넘지 못함).
 *
 * ⚠️ `ROLE_LEVEL[role]` 을 그대로 쓰면 `role='toString'` 같은 프로토타입 키가 함수(truthy)로
 *    잡혀 비교가 오염됩니다. 자기 키만 인정합니다(`constants/scanErrorMessages.js` 와 동일 관례).
 *
 * @param {*} role 비교할 역할 값(문자열이 아닐 수 있음 — `user?.role` 은 undefined 가능)
 * @returns {number} 0 | 1 | 2 | 3
 */
const levelOf = (role) => {
  if (typeof role !== 'string') return 0;
  if (!Object.prototype.hasOwnProperty.call(ROLE_LEVEL, role)) return 0;
  return ROLE_LEVEL[role];
};

/**
 * `role` 이 `minRole` **이상**인지 판정합니다(동등 포함).
 *
 * fail-safe 설계 — 다음은 **전부 `false`** 입니다:
 *  - `role` 이 미지 값 / `undefined` / `null` / 비문자열 (로그인 전·응답 누락)
 *  - `minRole` 이 미지 값 / `undefined` (오타난 기준선이 "무조건 통과" 로 열리는 fail-open 방지)
 *
 * @param {*} role 현재 사용자 역할 (`user?.role`)
 * @param {*} minRole 요구 최소 역할 (예: `'STAFF'`)
 * @returns {boolean}
 */
export const isAtLeast = (role, minRole) => {
  const min = levelOf(minRole);
  if (min === 0) return false;
  return levelOf(role) >= min;
};
